import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Search, Star, X, Layers, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { inr } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { slugify } from '@/data/nav';

const EMPTY = {
  name: '', slug: '', description: '', short_description: '', hero_image: '',
  images: [], product_ids: [], room_slug: '', discount_type: 'percent',
  discount_value: 10, is_active: true, is_featured: false, sort_order: 0, tags: [],
};

export default function AdminBundles() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [products, setProducts] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [rooms, setRooms] = useState([]);
useEffect(() => {
  api.get('/admin/categories', { params: { type: 'room' } }).then((r) => setRooms(r.data || [])).catch(() => {});
}, []);

  const load = () => {
    setLoading(true);
    api.get('/admin/bundles').then((r) => setBundles(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get('/admin/products', { params: { limit: 300 } }).then((r) => setProducts(r.data?.items || [])).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name, slug: b.slug, description: b.description || '',
      short_description: b.short_description || '',
      hero_image: b.hero_image || '', images: b.images || [],
      product_ids: b.product_ids || [], room_slug: b.room_slug || '',
      discount_type: b.discount_type || 'percent',
      discount_value: b.discount_value || 0,
      is_active: !!b.is_active, is_featured: !!b.is_featured,
      sort_order: b.sort_order || 0, tags: b.tags || [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error('Bundle name is required'); return; }
    if (!form.product_ids?.length) { toast.error('Add at least one product'); return; }
    const payload = { ...form, discount_value: Number(form.discount_value || 0), sort_order: Number(form.sort_order || 0) };
    try {
      if (editing) await api.put(`/admin/bundles/${editing.id}`, payload);
      else await api.post('/admin/bundles', payload);
      toast.success(editing ? 'Bundle updated' : 'Bundle created');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this bundle?')) return;
    try { await api.delete(`/admin/bundles/${id}`); toast.success('Bundle deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const filteredProducts = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => p.name.toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q));
  }, [products, pickerQuery]);

  const toggleProduct = (id) => {
    setForm((f) => {
      const has = f.product_ids.includes(id);
      const next = has ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id];
      return { ...f, product_ids: next, hero_image: f.hero_image || (products.find((p) => p.id === id)?.images?.[0] || '') };
    });
  };

  const field = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none';
  const selectedProducts = form.product_ids.map((id) => products.find((p) => p.id === id)).filter(Boolean);
  const selectedSubtotal = selectedProducts.reduce((s, p) => s + (p?.price || 0), 0);
  const previewDiscount = form.discount_type === 'percent'
    ? Math.round(selectedSubtotal * (Number(form.discount_value) || 0) / 100)
    : Number(form.discount_value) || 0;
  const previewPrice = Math.max(0, selectedSubtotal - previewDiscount);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Bundles</h1>
          <p className="text-sm text-muted-foreground">Complete room sets and curated collections with one-tap add-to-cart.</p>
        </div>
        <button onClick={openNew} data-testid="admin-bundle-new" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2.5 text-sm font-semibold text-white">
          <Plus size={16} /> New Bundle
        </button>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading bundles…</p>
      ) : bundles.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-white p-10 text-center">
          <Layers className="mx-auto mb-3 text-muted-foreground" size={28} />
          <p className="text-sm text-muted-foreground">No bundles yet. Create your first Complete Room Set.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-bundles-grid">
          {bundles.map((b) => (
            <div key={b.id} className="overflow-hidden rounded-2xl border border-border bg-white">
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-[hsl(var(--gaze-ivory))]">
                {b.hero_image && <img src={b.hero_image} alt={b.name} className="h-full w-full object-cover" />}
                <div className="absolute left-3 top-3 flex gap-1">
                  {b.is_featured && <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--gaze-gold))]/95 px-2 py-0.5 text-[10px] font-semibold text-black"><Star size={10} /> Featured</span>}
                  {!b.is_active && <span className="rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white">Inactive</span>}
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-lg leading-tight">{b.name}</h3>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(b)} className="grid h-8 w-8 place-items-center rounded-lg border border-border" data-testid="admin-bundle-edit"><Pencil size={13} /></button>
                    <button onClick={() => del(b.id)} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-destructive" data-testid="admin-bundle-delete"><Trash2 size={13} /></button>
                  </div>
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{b.short_description || b.description}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5">{b.product_count} products</span>
                  {b.room_slug && <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 capitalize">{b.room_slug.replace(/-/g, ' ')}</span>}
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-800">{b.stock > 0 ? `${b.stock} in stock` : 'Out of stock'}</span>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-lg font-bold">{inr(b.bundle_price)}</span>
                  {b.discount > 0 && <span className="text-xs text-muted-foreground line-through">{inr(b.subtotal)}</span>}
                  {b.discount > 0 && <span className="rounded-full bg-[hsl(var(--gaze-gold))]/20 px-2 py-0.5 text-[10px] font-semibold text-[hsl(var(--primary))]">Save {inr(b.discount)}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Bundle' : 'New Bundle'}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><label className="text-xs font-medium">Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="admin-bundle-name" /></div>
            <div><label className="text-xs font-medium">Slug (optional)</label><input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={field} placeholder="auto from name" /></div>
            <div><label className="text-xs font-medium">Room (optional)</label>
              <select value={form.room_slug || ''} onChange={(e) => setForm({ ...form, room_slug: e.target.value })} className={field}>
                <option value="">— None (general bundle) —</option>
                {rooms.filter((r) => r.active !== false).map((r) => <option key={r.slug} value={r.slug}>{r.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2"><label className="text-xs font-medium">Short description</label><input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className={field} /></div>
            <div className="sm:col-span-2"><label className="text-xs font-medium">Description</label><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={field} /></div>
            <div className="sm:col-span-2"><label className="text-xs font-medium">Hero image URL</label><input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} className={field} placeholder="https://..." /></div>
            <div><label className="text-xs font-medium">Discount type</label>
              <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className={field}>
                <option value="percent">% off</option>
                <option value="flat">Flat ₹ off</option>
                <option value="none">No discount</option>
              </select>
            </div>
            <div><label className="text-xs font-medium">Discount value</label><input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className={field} data-testid="admin-bundle-discount" /></div>
            <div><label className="text-xs font-medium">Sort order</label><input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className={field} /></div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} /> Featured</label>
            </div>

            {/* Product picker */}
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium">Products in this bundle ({form.product_ids.length})</label>
                <button onClick={() => setPickerOpen((v) => !v)} data-testid="admin-bundle-pick" className="text-xs font-semibold text-[hsl(var(--primary))]">{pickerOpen ? 'Close picker' : 'Add products'}</button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedProducts.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--gaze-ivory))] px-3 py-1 text-xs">
                    <img src={p.images?.[0]} alt="" className="h-5 w-5 rounded-full object-cover" />
                    <span className="max-w-[140px] truncate">{p.name}</span>
                    <button onClick={() => toggleProduct(p.id)}><X size={12} /></button>
                  </span>
                ))}
              </div>

              {pickerOpen && (
                <div className="mt-3 rounded-xl border border-border bg-white">
                  <div className="flex items-center gap-2 border-b border-border px-3">
                    <Search size={14} className="text-muted-foreground" />
                    <input value={pickerQuery} onChange={(e) => setPickerQuery(e.target.value)} placeholder="Search products by name or SKU" className="w-full bg-transparent py-2 text-sm outline-none" />
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {filteredProducts.slice(0, 100).map((p) => {
                      const on = form.product_ids.includes(p.id);
                      return (
                        <button key={p.id} onClick={() => toggleProduct(p.id)} className={`flex w-full items-center gap-3 border-b border-border px-3 py-2 text-left text-sm last:border-0 hover:bg-[hsl(var(--gaze-ivory))]`}>
                          <img src={p.images?.[0]} alt="" className="h-9 w-9 rounded-lg object-cover" />
                          <div className="flex-1"><p className="font-medium">{p.name}</p><p className="text-xs text-muted-foreground">{p.sku} · {inr(p.price)} · {p.stock} in stock</p></div>
                          <span className={`grid h-6 w-6 place-items-center rounded-full border ${on ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-border text-transparent'}`}><Check size={13} /></span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="sm:col-span-2 rounded-xl bg-[hsl(var(--gaze-ivory))] p-4 text-sm">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Bundle preview</p>
              <div className="mt-1 flex flex-wrap items-baseline gap-3">
                <span className="text-2xl font-bold">{inr(previewPrice)}</span>
                {previewDiscount > 0 && <span className="text-sm text-muted-foreground line-through">{inr(selectedSubtotal)}</span>}
                {previewDiscount > 0 && <span className="rounded-full bg-white px-2 py-0.5 text-xs">Save {inr(previewDiscount)}</span>}
                <span className="ml-auto text-xs text-muted-foreground">{form.product_ids.length} products</span>
              </div>
            </div>
          </div>
          <button onClick={save} data-testid="admin-bundle-save" className="mt-4 w-full rounded-full bg-[hsl(var(--primary))] py-3 text-sm font-semibold text-white">{editing ? 'Update' : 'Create'} Bundle</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
